// Client-side preparation of the single uploaded label image (M4).
//
// The picked file is checked (type, 10 MB), decoded, and re-encoded on a
// canvas at no more than 2048 px on the long edge: PNG when the source has
// any transparency, otherwise JPEG at 0.92. The result is what the studio
// draws and what it uploads as `uploaded_image`.
//
// SSR-safe: every browser API is touched inside the functions only.

export const ACCEPTED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"] as const
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024
export const MAX_IMAGE_EDGE = 2048

export type PreparedImage = {
  file: File
  image: HTMLImageElement
  width: number
  height: number
}

export class ImageUploadError extends Error {}

export function validateImageFile(file: File): string | null {
  if (!(ACCEPTED_IMAGE_TYPES as readonly string[]).includes(file.type)) {
    return "Please choose a PNG, JPEG or WebP image."
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return "That image is larger than 10 MB. Please choose a smaller file."
  }
  return null
}

export function loadImage(src: string, crossOrigin = false): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    if (crossOrigin) img.crossOrigin = "anonymous"
    img.decoding = "async"
    img.onload = () => resolve(img)
    img.onerror = () => reject(new ImageUploadError("The image could not be read."))
    img.src = src
  })
}

function hasTransparency(ctx: CanvasRenderingContext2D, width: number, height: number) {
  // Sample on a coarse grid; a logo with a transparent ground trips this
  // almost immediately, and a photo never does.
  const data = ctx.getImageData(0, 0, width, height).data
  const step = Math.max(1, Math.floor((width * height) / 250_000)) * 4
  for (let i = 3; i < data.length; i += step) {
    if (data[i] < 250) return true
  }
  return false
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new ImageUploadError("The image could not be prepared."))),
      type,
      quality,
    )
  })
}

export async function prepareImage(file: File): Promise<PreparedImage> {
  const problem = validateImageFile(file)
  if (problem) throw new ImageUploadError(problem)

  const sourceUrl = URL.createObjectURL(file)
  try {
    const source = await loadImage(sourceUrl)
    const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(source.naturalWidth, source.naturalHeight))
    const width = Math.max(1, Math.round(source.naturalWidth * scale))
    const height = Math.max(1, Math.round(source.naturalHeight * scale))

    const canvas = document.createElement("canvas")
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext("2d", { willReadFrequently: true })
    if (!ctx) throw new ImageUploadError("Your browser could not prepare the image.")
    ctx.imageSmoothingQuality = "high"
    ctx.drawImage(source, 0, 0, width, height)

    const alpha = file.type !== "image/jpeg" && hasTransparency(ctx, width, height)
    const type = alpha ? "image/png" : "image/jpeg"
    const blob = await canvasToBlob(canvas, type, alpha ? undefined : 0.92)
    const base = file.name.replace(/\.[^.]+$/, "") || "label-image"
    const prepared = new File([blob], `${base}.${alpha ? "png" : "jpg"}`, { type })
    const image = await loadImage(URL.createObjectURL(prepared))
    return { file: prepared, image, width, height }
  } finally {
    URL.revokeObjectURL(sourceUrl)
  }
}
